import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaCircle as FaCircleRaw,
  FaEllipsisV as FaEllipsisVRaw,
  FaPaperPlane as FaPaperPlaneRaw,
  FaPen as FaPenRaw,
  FaSearch as FaSearchRaw,
  FaSmile as FaSmileRaw,
  FaUser as FaUserRaw,
  FaUsers as FaUsersRaw,
} from 'react-icons/fa';
import LinkedText from '../../components/LinkedText';
import { useAuth } from '../../context/AuthContext';
import { teacherService } from '../../services/classroom/teacherService';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCircle: any = FaCircleRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEllipsisV: any = FaEllipsisVRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPaperPlane: any = FaPaperPlaneRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPen: any = FaPenRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSmile: any = FaSmileRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUser: any = FaUserRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUsers: any = FaUsersRaw;

const ACCENT = 'var(--primary)';
const MOBILE_BREAKPOINT = '960px';

const Shell = styled.div`
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  border: 1px solid var(--outline);
  border-radius: 16px;
  overflow: hidden;
  /* Cap height so long threads scroll inside the message pane */
  height: min(720px, calc(100vh - 160px));
  max-height: min(720px, calc(100vh - 160px));
  min-height: 360px;
  width: 100%;
  align-self: stretch;
  background: var(--surface);
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
    height: auto;
    max-height: none;
    min-height: auto;
  }
`;

const Sidebar = styled.aside`
  border-right: 1px solid var(--outline);
  background: var(--surfaceVariant);
  display: flex;
  flex-direction: column;
  min-height: 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    border-right: none;
    border-bottom: 1px solid var(--outline);
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1rem 0.8rem;
`;

const SidebarTitle = styled.h2`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
`;

const NewChatButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: none;
  background: ${ACCENT};
  color: var(--onPrimary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SearchWrap = styled.div`
  margin: 0 1rem;
  position: relative;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--textSecondary);
  font-size: 0.78rem;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.5rem 0.7rem 0.5rem 2rem;
  font-size: 0.84rem;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0.45rem;
  padding: 0.85rem 1rem 0.6rem;
`;

const FilterTab = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? ACCENT : 'var(--outline)')};
  background: ${({ $active }) => ($active ? ACCENT : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--textSecondary)')};
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
`;

const ConversationList = styled.div`
  padding: 0.25rem 0.45rem 0.65rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const ConversationRow = styled.button<{ $active: boolean }>`
  width: 100%;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(45,137,239,0.4)' : 'transparent')};
  background: ${({ $active }) => ($active ? ACCENT : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--text)')};
  border-radius: 12px;
  padding: 0.65rem 0.55rem;
  text-align: left;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  gap: 0.65rem;
  cursor: pointer;
  &:hover {
    background: var(--surface);
  }
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8a94a7;
`;

const ActiveAvatar = styled(Avatar)`
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.95);
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #26a65b;
  margin-top: 28px;
  margin-left: -8px;
  border: 2px solid var(--surfaceVariant);
`;

const RowName = styled.div`
  font-size: 0.98rem;
  font-weight: 700;
`;

const RowPreview = styled.div<{ $active: boolean }>`
  font-size: 0.8rem;
  color: ${({ $active }) => ($active ? 'rgba(255,255,255,0.92)' : 'var(--textSecondary)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowTime = styled.div<{ $active: boolean }>`
  font-size: 0.72rem;
  color: ${({ $active }) => ($active ? 'rgba(255,255,255,0.88)' : 'var(--textSecondary)')};
  white-space: nowrap;
`;

const Main = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 0;
  height: 100%;
  background: var(--surface);
`;

const MainHeader = styled.header`
  border-bottom: 1px solid var(--outline);
  padding: 0.9rem 1.15rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const HeaderIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeaderName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  color: var(--text);
`;

const HeaderStatus = styled.div`
  font-size: 0.8rem;
  color: #26a65b;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  color: var(--textSecondary);
  font-size: 0.86rem;
`;

const ProfileLink = styled.button`
  border: none;
  background: transparent;
  color: inherit;
  font-weight: 600;
  cursor: pointer;
`;

const Thread = styled.div`
  padding: 1.2rem 1.45rem;
  min-height: 0;
  overflow-y: auto;
  background: var(--surface);
`;

const DayMarker = styled.div`
  text-align: center;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  color: var(--textSecondary);
  font-weight: 700;
  margin-bottom: 1.1rem;
`;

const BubbleWrap = styled.div<{ $mine: boolean }>`
  display: flex;
  justify-content: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
  margin-bottom: 1rem;
`;

const Bubble = styled.div<{ $mine: boolean }>`
  max-width: min(74%, 640px);
  border: 1px solid ${({ $mine }) => ($mine ? 'transparent' : 'var(--outline)')};
  background: ${({ $mine }) => ($mine ? ACCENT : 'var(--surface)')};
  color: ${({ $mine }) => ($mine ? 'var(--onPrimary)' : 'var(--text)')};
  border-radius: 12px;
  padding: 0.8rem 0.95rem;
  line-height: 1.55;
  font-size: 0.94rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const BubbleMeta = styled.div<{ $mine: boolean }>`
  margin-top: 0.35rem;
  font-size: 0.7rem;
  color: ${({ $mine }) => ($mine ? 'rgba(255,255,255,0.85)' : 'var(--textSecondary)')};
  text-align: ${({ $mine }) => ($mine ? 'right' : 'left')};
`;

const Composer = styled.form`
  border-top: 1px solid var(--outline);
  padding: 1rem 1.1rem;
  display: grid;
  gap: 0.55rem;
`;

const ComposerInput = styled.textarea`
  width: 100%;
  min-height: 84px;
  resize: vertical;
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  font: inherit;
`;

const ComposerActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ComposerTools = styled.div`
  display: inline-flex;
  gap: 0.65rem;
  color: var(--textSecondary);
`;

const ToolButton = styled.button`
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
`;

const SendButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.62rem 1.15rem;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--onPrimary);
  background: ${ACCENT};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusBanner = styled.div<{ $tone: 'success' | 'error' }>`
  margin: 0 1.1rem 0.75rem;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  font-size: 0.84rem;
  background: ${({ $tone }) => ($tone === 'success' ? 'rgba(38,166,91,0.12)' : 'rgba(220,38,38,0.1)')};
  color: ${({ $tone }) => ($tone === 'success' ? '#1f7a45' : '#b42318')};
  border: 1px solid ${({ $tone }) => ($tone === 'success' ? 'rgba(38,166,91,0.35)' : 'rgba(220,38,38,0.25)')};
`;

const BroadcastHint = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 1.25rem 1.1rem;
  border: 1px dashed var(--outline);
  border-radius: 14px;
  color: var(--textSecondary);
  font-size: 0.9rem;
  line-height: 1.55;
  text-align: center;
  background: var(--surfaceVariant);
`;

interface ChatTarget {
  id: string;
  label: string;
  kind: 'students' | 'classes' | 'faculty';
  online: boolean;
  subtitle?: string;
}

function inThread(
  message: { sender_id: string; receiver_id: string },
  currentUserId: string,
  peerId: string
) {
  return (
    (message.sender_id === currentUserId && message.receiver_id === peerId) ||
    (message.sender_id === peerId && message.receiver_id === currentUserId)
  );
}

function formatRowTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const ClassroomTeacherCommunicationPage: React.FC = () => {
  const { user } = useAuth();
  const teacherId = user?.id ?? '';
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [messageBody, setMessageBody] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState<'all' | 'students' | 'classes' | 'faculty'>('all');
  const [activeChatId, setActiveChatId] = useState('');
  const [activeClassId, setActiveClassId] = useState('');
  const [broadcastStatus, setBroadcastStatus] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);

  const isBroadcastMode = filter === 'classes';

  const contactsQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'message-contacts', teacherId],
    queryFn: () => teacherService.getStudentContacts(),
    enabled: Boolean(teacherId),
  });

  const classesQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'broadcast-classes', teacherId],
    queryFn: () => teacherService.getTeacherClassesForBroadcast(),
    enabled: Boolean(teacherId),
  });

  const messagesQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'messages', teacherId],
    queryFn: () => teacherService.getMessages(teacherId),
    refetchInterval: 15000,
    enabled: Boolean(teacherId),
  });

  useEffect(() => {
    if (!teacherId) return;
    return teacherService.subscribeToMessages(teacherId, () => {
      void messagesQuery.refetch();
    });
  }, [messagesQuery, teacherId]);

  useEffect(() => {
    const classId = searchParams.get('classId')?.trim() ?? '';
    const mode = searchParams.get('mode')?.trim() ?? '';
    if (!classId) return;
    setFilter('classes');
    setActiveClassId(classId);
    if (mode === 'broadcast') {
      setBroadcastStatus(null);
    }
  }, [searchParams]);

  const sendMessage = useMutation({
    mutationFn: teacherService.sendMessage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'messages', teacherId] });
      setMessageBody('');
    },
  });

  const sendBroadcast = useMutation({
    mutationFn: teacherService.sendClassBroadcast,
    onSuccess: (result) => {
      if (!result.ok) {
        setBroadcastStatus({ tone: 'error', message: result.error ?? 'Broadcast failed.' });
        return;
      }
      const skipped = Number(result.skipped_count ?? 0);
      const sent = Number(result.sent_count ?? 0);
      const skippedNote = skipped > 0 ? ` (${skipped} skipped)` : '';
      setBroadcastStatus({
        tone: 'success',
        message: sent > 0 ? `Sent to ${sent} student${sent === 1 ? '' : 's'}${skippedNote}.` : `No messages sent${skippedNote}.`,
      });
      setMessageBody('');
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'messages', teacherId] });
    },
    onError: (error: Error) => {
      setBroadcastStatus({ tone: 'error', message: error.message || 'Broadcast failed.' });
    },
  });

  const chatTargets = useMemo<ChatTarget[]>(() => {
    const rows = contactsQuery.data ?? [];
    return rows.map((row) => ({
      id: row.id,
      label: row.full_name,
      kind: 'students' as const,
      online: false,
    }));
  }, [contactsQuery.data]);

  const classTargets = useMemo<ChatTarget[]>(() => {
    const rows = classesQuery.data ?? [];
    return rows.map((row) => ({
      id: row.id,
      label: row.name,
      kind: 'classes' as const,
      online: false,
      subtitle: `${row.studentCount} student${row.studentCount === 1 ? '' : 's'}`,
    }));
  }, [classesQuery.data]);

  useEffect(() => {
    if (isBroadcastMode) return;
    const hasActive = chatTargets.some((target) => target.id === activeChatId);
    if (!hasActive) {
      setActiveChatId(chatTargets[0]?.id ?? '');
    }
  }, [activeChatId, chatTargets, isBroadcastMode]);

  useEffect(() => {
    if (!isBroadcastMode) return;
    const hasActive = classTargets.some((target) => target.id === activeClassId);
    if (!hasActive) {
      setActiveClassId(classTargets[0]?.id ?? '');
    }
  }, [activeClassId, classTargets, isBroadcastMode]);

  const sidebarTargets = isBroadcastMode ? classTargets : chatTargets;

  const filteredTargets = useMemo(
    () =>
      sidebarTargets.filter((target) => {
        if (!isBroadcastMode && filter === 'students' && target.kind !== 'students') return false;
        if (!isBroadcastMode && filter === 'faculty' && target.kind !== 'faculty') return false;
        if (searchText.trim()) {
          const haystack = `${target.label} ${target.subtitle ?? ''}`.toLowerCase();
          return haystack.includes(searchText.trim().toLowerCase());
        }
        return true;
      }),
    [filter, isBroadcastMode, searchText, sidebarTargets],
  );

  const activeTarget = chatTargets.find((target) => target.id === activeChatId) ?? null;
  const activeClass = classTargets.find((target) => target.id === activeClassId) ?? null;
  const lastMessageByPeer = useMemo(() => {
    const rows = [...(messagesQuery.data ?? [])].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const map = new Map<string, (typeof rows)[number]>();
    rows.forEach((message) => {
      const peerId =
        message.sender_id === teacherId
          ? message.receiver_id
          : message.receiver_id === teacherId
            ? message.sender_id
            : null;
      if (peerId && !map.has(peerId)) {
        map.set(peerId, message);
      }
    });
    return map;
  }, [messagesQuery.data, teacherId]);

  const thread = useMemo(() => {
    if (!activeTarget || !teacherId) return [];
    const rows = messagesQuery.data ?? [];
    return rows
      .filter((message) => inThread(message, teacherId, activeTarget.id))
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [activeTarget, messagesQuery.data, teacherId]);

  const onMessageSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!messageBody.trim() || !teacherId) return;

    if (isBroadcastMode) {
      if (!activeClass) return;
      const studentCount = classesQuery.data?.find((row) => row.id === activeClass.id)?.studentCount ?? 0;
      const confirmed = window.confirm(
        `Send this message to all ${studentCount} enrolled student${studentCount === 1 ? '' : 's'} in ${activeClass.label}?`
      );
      if (!confirmed) return;
      setBroadcastStatus(null);
      sendBroadcast.mutate({
        classId: activeClass.id,
        content: messageBody.trim(),
      });
      return;
    }

    if (!activeTarget) return;
    sendMessage.mutate({
      sender_id: teacherId,
      receiver_id: activeTarget.id,
      content: messageBody.trim(),
    });
  };

  const sidebarLoading = isBroadcastMode ? classesQuery.isLoading : contactsQuery.isLoading;
  const sidebarEmptyLabel = isBroadcastMode
    ? sidebarLoading
      ? 'Loading classes...'
      : 'No classes found.'
    : sidebarLoading
      ? 'Loading students...'
      : 'No students found.';

  return (
    <Shell>
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>Messages</SidebarTitle>
          <NewChatButton type="button" aria-label="Start new chat">
            <FaPen />
          </NewChatButton>
        </SidebarHeader>
        <SearchWrap>
          <SearchIcon>
            <FaSearch />
          </SearchIcon>
          <SearchInput
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder={isBroadcastMode ? 'Search classes...' : 'Search conversations...'}
            aria-label={isBroadcastMode ? 'Search classes' : 'Search conversations'}
          />
        </SearchWrap>
        <FilterTabs>
          <FilterTab
            type="button"
            $active={filter === 'all'}
            onClick={() => {
              setFilter('all');
              setBroadcastStatus(null);
            }}
          >
            All
          </FilterTab>
          <FilterTab
            type="button"
            $active={filter === 'students'}
            onClick={() => {
              setFilter('students');
              setBroadcastStatus(null);
            }}
          >
            Students
          </FilterTab>
          <FilterTab
            type="button"
            $active={filter === 'classes'}
            onClick={() => {
              setFilter('classes');
              setBroadcastStatus(null);
            }}
          >
            Classes
          </FilterTab>
          <FilterTab
            type="button"
            $active={filter === 'faculty'}
            onClick={() => {
              setFilter('faculty');
              setBroadcastStatus(null);
            }}
          >
            Faculty
          </FilterTab>
        </FilterTabs>
        <ConversationList>
          {filteredTargets.length === 0 ? (
            <div style={{ padding: '0.8rem', color: 'var(--textSecondary)', fontSize: '0.82rem' }}>
              {sidebarEmptyLabel}
            </div>
          ) : null}
          {filteredTargets.map((target) => {
            const isActive = isBroadcastMode ? target.id === activeClassId : target.id === activeChatId;
            return (
            <ConversationRow
              key={target.id}
              type="button"
              $active={isActive}
              onClick={() => {
                if (isBroadcastMode) {
                  setActiveClassId(target.id);
                  setBroadcastStatus(null);
                  return;
                }
                setActiveChatId(target.id);
              }}
            >
              <div style={{ position: 'relative' }}>
                {isActive ? (
                  <ActiveAvatar>
                    {target.kind === 'classes' ? <FaUsers /> : <FaUser />}
                  </ActiveAvatar>
                ) : (
                  <Avatar>
                    {target.kind === 'classes' ? <FaUsers /> : <FaUser />}
                  </Avatar>
                )}
                {target.online ? <Dot /> : null}
              </div>
              <div>
                <RowName>{target.label}</RowName>
                <RowPreview $active={isActive}>
                  {isBroadcastMode
                    ? target.subtitle ?? 'Class broadcast'
                    : lastMessageByPeer.get(target.id)?.content ?? 'No messages yet'}
                </RowPreview>
              </div>
              <RowTime $active={isActive}>
                {isBroadcastMode
                  ? '—'
                  : lastMessageByPeer.get(target.id)?.created_at
                    ? formatRowTime(lastMessageByPeer.get(target.id)!.created_at)
                    : '—'}
              </RowTime>
            </ConversationRow>
            );
          })}
        </ConversationList>
      </Sidebar>

      <Main>
        <MainHeader>
          <HeaderIdentity>
            <Avatar>
              {isBroadcastMode ? <FaUsers /> : <FaUser />}
            </Avatar>
            <div>
              <HeaderName>
                {isBroadcastMode
                  ? activeClass?.label ?? 'Select a class'
                  : activeTarget?.label ?? 'Select a student'}
              </HeaderName>
              <HeaderStatus>
                <FaCircle style={{ fontSize: '0.55rem', color: isBroadcastMode ? ACCENT : '#26a65b' }} />
                {isBroadcastMode
                  ? activeClass
                    ? `Message all ${classesQuery.data?.find((row) => row.id === activeClass.id)?.studentCount ?? 0} students`
                    : 'No class selected'
                  : activeTarget
                    ? activeTarget.online
                      ? 'Online'
                      : 'Offline'
                    : 'No contact selected'}
              </HeaderStatus>
            </div>
          </HeaderIdentity>
          <HeaderActions>
            {!isBroadcastMode ? <ProfileLink type="button">View Profile</ProfileLink> : null}
            <FaEllipsisV />
          </HeaderActions>
        </MainHeader>

        {broadcastStatus ? (
          <StatusBanner $tone={broadcastStatus.tone}>{broadcastStatus.message}</StatusBanner>
        ) : null}

        <Thread>
          {isBroadcastMode ? (
            <>
              <DayMarker>CLASS BROADCAST</DayMarker>
              {!activeClass ? (
                <BroadcastHint>Select a class to send a message to all enrolled students at once.</BroadcastHint>
              ) : (
                <BroadcastHint>
                  Compose a message below to send it privately to every student enrolled in{' '}
                  <strong>{activeClass.label}</strong>. Each student will see it in their Messages thread with you.
                </BroadcastHint>
              )}
            </>
          ) : (
            <>
              <DayMarker>{thread.length ? 'CONVERSATION' : 'NO MESSAGES YET'}</DayMarker>
              {!activeTarget ? (
                <BubbleWrap $mine={false}>
                  <Bubble $mine={false}>Select a student to start messaging.</Bubble>
                </BubbleWrap>
              ) : thread.length === 0 ? (
                <BubbleWrap $mine={false}>
                  <Bubble $mine={false}>Start a conversation with {activeTarget.label}.</Bubble>
                </BubbleWrap>
              ) : (
                <>
                  {thread.map((message, index) => {
                    const mine = message.sender_id === teacherId;
                    return (
                      <BubbleWrap key={message.id} $mine={mine}>
                        <div>
                          <Bubble $mine={mine}>
                            <LinkedText text={message.content} inverted={mine} />
                          </Bubble>
                          <BubbleMeta $mine={mine}>
                            {new Date(message.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                            {mine && index === thread.length - 1 ? '  •  Read' : ''}
                          </BubbleMeta>
                        </div>
                      </BubbleWrap>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Thread>

        <Composer onSubmit={onMessageSubmit}>
          <ComposerInput
            rows={3}
            value={messageBody}
            onChange={(event) => setMessageBody(event.target.value)}
            placeholder={
              isBroadcastMode
                ? activeClass
                  ? `Message all students in ${activeClass.label}...`
                  : 'Select a class to broadcast'
                : activeTarget
                  ? 'Type your message here...'
                  : 'Select a student to message'
            }
          />
          <ComposerActions>
            <ComposerTools>
              {!isBroadcastMode ? (
                <>
                  <ToolButton type="button" aria-label="Attach file">
                    📎
                  </ToolButton>
                  <ToolButton type="button" aria-label="Emoji picker">
                    <FaSmile />
                  </ToolButton>
                  <ToolButton type="button" aria-label="Add image">
                    🖼️
                  </ToolButton>
                </>
              ) : null}
            </ComposerTools>
            <SendButton
              type="submit"
              disabled={
                (isBroadcastMode ? sendBroadcast.isPending : sendMessage.isPending) ||
                !messageBody.trim() ||
                !teacherId ||
                (isBroadcastMode ? !activeClass : !activeTarget)
              }
            >
              {isBroadcastMode
                ? sendBroadcast.isPending
                  ? 'Sending...'
                  : 'Send to All Students'
                : sendMessage.isPending
                  ? 'Sending...'
                  : 'Send Message'}
              <FaPaperPlane />
            </SendButton>
          </ComposerActions>
        </Composer>
      </Main>
    </Shell>
  );
};

export default ClassroomTeacherCommunicationPage;
